import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  DriverDataBrief,
  LohiDashboardService,
  LoHiSandiLoadNeedingAdminApproval,
} from 'src/app/dashboard-2025/lohi-dashboard.service';
import { SnackBarService } from '../snackbar.service';

export interface LoadApprovalDialogData {
  load: LoHiSandiLoadNeedingAdminApproval;
  availableDrivers: DriverDataBrief[];
}

@Component({
  selector: 'lohi-app-load-approval-dialog',
  templateUrl: './load-approval-dialog.component.html',
  styleUrls: ['./load-approval-dialog.component.scss'],
})
export class LoadApprovalDialogComponent {
  public isLoading = false;
  public swapMode = false;
  public selectedSwapDriverId: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<LoadApprovalDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LoadApprovalDialogData,
    private dashboardService: LohiDashboardService,
    private snackBar: SnackBarService,
  ) {}

  public get showSwapDriverOption(): boolean {
    return (this.data.availableDrivers?.length ?? 0) > 0;
  }

  public trackByDriverId(index: number, driver: DriverDataBrief): string {
    return driver.id;
  }

  public onApprove(): void {
    this.submitApproval(true);
  }

  public onReject(): void {
    this.submitApproval(false);
  }

  public enterSwapMode(): void {
    this.swapMode = true;
    this.selectedSwapDriverId = null;
  }

  public exitSwapMode(): void {
    this.swapMode = false;
    this.selectedSwapDriverId = null;
  }

  public onAcceptAndSwapDriver(): void {
    if (!this.selectedSwapDriverId || this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.dashboardService
      .submitAdminApprovalResponse$(this.data.load.uniqueId, true, this.selectedSwapDriverId)
      .subscribe({
        next: () => {
          this.snackBar.showMessage('Load accepted and assigned to the selected driver.', 3000);
          this.dialogRef.close({ success: true });
        },
        error: (err) => {
          console.error('Failed to submit approval with driver swap:', err);
          this.snackBar.showError(this.formatSubmitError(err));
          this.isLoading = false;
        },
      });
  }

  private submitApproval(approved: boolean): void {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.dashboardService.submitAdminApprovalResponse$(this.data.load.uniqueId, approved).subscribe({
      next: () => {
        this.snackBar.showMessage(approved ? 'Load approved successfully.' : 'Load rejected.', 3000);
        this.dialogRef.close({ success: true });
      },
      error: (err) => {
        console.error('Failed to submit approval response:', err);
        this.snackBar.showError(this.formatSubmitError(err));
        this.isLoading = false;
      },
    });
  }

  private formatSubmitError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (typeof body === 'string' && body.trim()) {
        return body;
      }
      if (body && typeof body === 'object') {
        const messageToUser = (body as { messageToUser?: string }).messageToUser;
        if (typeof messageToUser === 'string' && messageToUser.trim()) {
          return messageToUser;
        }
        const message = (body as { message?: string }).message;
        if (typeof message === 'string' && message.trim()) {
          return message;
        }
      }
      if (err.status === 0) {
        return 'Network error. Check your connection and try again.';
      }
      if (err.status >= 500) {
        return 'Server error. Please try again in a moment.';
      }
    }
    return 'We could not update this load. Please try again.';
  }

  public onCancel(): void {
    this.dialogRef.close();
  }
}
